
import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Phone, Mail } from 'lucide-react';
import MemberContactInfo from './MemberContactInfo';
import FriendshipButton from './FriendshipButton';
import { Member } from '../types';
import { getBadgeLevel } from '@/utils/referralUtils';

interface MemberRowProps {
  member: Member & { invite_count?: number; nickname?: string | null; avatar_url?: string | null };
  userId: string | undefined;
  isFriend?: boolean;
  isPendingFriend?: boolean;
  isAdmin: boolean;
  onSendRequest?: (recipientId: string) => void;
  onAcceptRequest?: (requesterId: string) => void;
}

const MemberRow = ({ 
  member, 
  userId,
  isFriend,
  isPendingFriend,
  isAdmin,
  onSendRequest,
  onAcceptRequest
}: MemberRowProps) => {
  const [showContact, setShowContact] = useState(false);
  
  const handleToggleContact = () => setShowContact(!showContact);
  
  const displayName = member.nickname || member.full_name;
  const badge = getBadgeLevel(member.invite_count || 0);
  
  const getBadgeColor = (level: string) => {
    switch (level) {
      case 'bronze': return 'bg-amber-600 hover:bg-amber-700';
      case 'silver': return 'bg-gray-400 hover:bg-gray-500';
      case 'gold': return 'bg-yellow-500 hover:bg-yellow-600';
      case 'platinum': return 'bg-cyan-500 hover:bg-cyan-600';
      case 'legend': return 'bg-purple-600 hover:bg-purple-700';
      default: return undefined;
    }
  };
  
  return (
    <tr className="border-b hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar>
            {member.avatar_url ? (
              <AvatarImage src={member.avatar_url} alt={displayName} />
            ) : null}
            <AvatarFallback>{displayName.substring(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{displayName}</p>
            {member.invite_count && member.invite_count > 0 && (
              <Badge className={`text-white text-xs ${getBadgeColor(badge.level)}`}>
                {badge.label}
              </Badge>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">{member.state}</td>
      <td className="px-4 py-3 hidden lg:table-cell">{member.local_government}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-1"
            onClick={handleToggleContact}
          >
            {showContact ? (
              <>
                <Eye className="h-4 w-4" /> Hide
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" /> View
              </>
            )}
          </Button>
          
          {userId !== member.id && (
            <FriendshipButton 
              userId={userId}
              memberId={member.id}
              isFriend={isFriend}
              isPendingFriend={isPendingFriend}
              onSendRequest={onSendRequest}
              onAcceptRequest={onAcceptRequest}
            />
          )}
        </div>
        
        {showContact && (
          <MemberContactInfo 
            email={member.email}
            phone={member.phone_number}
            canView={isFriend || false}
            isAdmin={isAdmin}
          />
        )}
      </td>
    </tr>
  );
};

export default MemberRow;
